import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, lastValueFrom, Observable, of, Subject, Subscription, take } from 'rxjs';
import { ScimIntegration, Environment, ENVIRONMENT, NetworkV2, ScimIntegrationUpdate, IntegrationAuthRequest, IntegrationAuthResponse } from '@netfoundry-ui/shared/model';
import { ApiService, FeatureService, HTTP_CLIENT, LoggerService, RefresherService } from '@netfoundry-ui/shared/services';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { TableFilterService } from '@netfoundry-ui/feature/shared-services';
import _ from 'lodash';
import { PagedGetOption, Sort, SortOrder, HateoasResourceOperation, PagedResourceCollection } from '@lagoshny/ngx-hateoas-client';
import { tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ScimIntegrationService extends HateoasResourceOperation<ScimIntegration> {
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 1,
            size: 30,
        }
    };
    page = 1;
    pageSize = 30;
    currentNetwork = new BehaviorSubject<NetworkV2>(new NetworkV2());
    unFilteredRecords = new BehaviorSubject<ScimIntegration[]>([]);
    filteredRecords = new BehaviorSubject<ScimIntegration[]>([]);
    allToggled = false;
    lastPageCount = 0;
    lastTotalCount = 0;

    private anyToggled = false;
    filterString = '';
    enableDeleteProtection = false;
    selectedRecordIds: string[] = [];
    isDeleteAction = false;
    hideAppButton = false;
    columnFilters: any = { name: '' };
    sorting = 'name';
    ordering: SortOrder = 'ASC';
    paramList: { size: number; sort: Sort } = {
        size: this.pageSize,
        sort: { name: 'ASC' },
    };
    isLoading = new BehaviorSubject<boolean>(true);
    filterHasChanged = new BehaviorSubject<boolean>(false);
    pageHasChanged = new BehaviorSubject<boolean>(false);
    confirmDelete = new Subject<ScimIntegration>();

    subscription: Subscription | undefined;
    debouncedRefreshRecords = _.debounce(() => this.refreshRecords(), 400);

    apiUrl: string | undefined;

    integrationClientUrl: string | undefined;

    constructor(
            private logger: LoggerService,
            private authorizationService: AuthorizationService,
            @Inject(HTTP_CLIENT) private http: HttpClient,
            @Inject(ENVIRONMENT) private environment: Environment,
            private refresher: RefresherService,
            private apiService: ApiService,
            private filterService: TableFilterService,
            private featureSvc: FeatureService
        ) {
            super(ScimIntegration);
            this.apiUrl = environment.integrationsManagementUrl;
            this.integrationClientUrl = environment.integrationsClientUrl;
            this.init();
        }

    init() {
        this.isLoading.next(true);
        this.clearSelected();
        this.filterService.setPageSize(this.pageSize);
        this.subscription = new Subscription();
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((currentNetwork) => {
                this.currentNetwork.next(currentNetwork);
                this.filterString = '';
                this.filterService.setPage(1, true);
                this.page = 1;
                this.filterHasChanged.next(true);
                this.pageHasChanged.next(true);
                this.debouncedRefreshRecords();
            })
        );
    }

    reset() {
        this.columnFilters = { name: '' };
        this.subscription?.unsubscribe();
        this.filterString = '';
        this.filterService.reset();
        this.debouncedRefreshRecords();
    }

    sort(sortBy: string, ordering: string) {

        

        if (this.sorting === sortBy) {
            this.ordering = ordering === 'asc' ? 'ASC' : ordering === 'desc' ? 'DESC' : (ordering as SortOrder);
        } else {
            this.ordering = 'ASC';
            this.sorting = sortBy;
        }

        const s: Sort = {};
        s[this.sorting] = this.ordering;
        this.paramList.sort = s;
        this.pageHasChanged.next(true);
        this.debouncedRefreshRecords();
    }

    override getResource(): Observable<ScimIntegration> {
        throw new Error('Do not use: see get getNetwork');
    }

    override getPage(): Observable<PagedResourceCollection<ScimIntegration>> {
        throw new Error('Do not use: see getNetworks');
    }

    private refreshRecords() {
        this.isLoading.next(true);
        const currentNetwork = this.currentNetwork.value;
        if (currentNetwork?.id) {
            if ((this.columnFilters.name || this.columnFilters.integrationKey) && this.page !== 1 && !this.pageHasChanged.getValue()) {
                this.filterService.setPage(0, true);
                this.page = 1;
            }
            this.getIntegrations(this.getOptions())
                .then((results) => {
                    this.unFilteredRecords.next(results);
                    this.filterService.setTotalPages(this.lastPageCount);
                    // if the number of pages has decreased and the user is past the last page now
                    if (this.page > this.lastPageCount && this.page !== 1) {
                        // move back one page
                        this.filterService.setPage(this.lastPageCount -1 , true);
                        this.page--;
                        this.debouncedRefreshRecords();
                        this.selectedRecordIds = [];
                    } else {
                        const filteredItems = results.map((rec: any) => {
                            rec.actionList = this.getAllowedActions(rec.id);
                            return rec;
                        });
                        this.filteredRecords.next(filteredItems || []);
                        this.filterService.setTotalElements(this.lastTotalCount);
                        this.isLoading.next(false);
                        this.filterHasChanged.next(false);
                        this.pageHasChanged.next(false);
                    }

                    
                })
                .catch(() => {
                    this.isLoading.next(false);
                    this.filterHasChanged.next(false);
                    this.pageHasChanged.next(false);
                });
        }
    }

    async getIntegrationsPage(options?: PagedGetOption): Promise<ScimIntegration[]> {
    
        return super
            .getPage({ ...ScimIntegrationService.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((integrations) => integrations?.resources || ([] as ScimIntegration[]));
    }

    async getIntegrations(options?: PagedGetOption): Promise<ScimIntegration[]> {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const url = `${this.apiUrl}integrations`;
        const pageParams = options?.pageParams || {};
        return this.http
            .get(url, {
                headers: headers,
                params: { ...pageParams, ...options?.params },
                
            })
            .toPromise()
            .then((result: any) => {
                this.lastPageCount = result?.page?.totalPages;
                this.lastTotalCount = result?.page?.totalElements;
                const integrations: any = (result?._embedded?.scimIntegrations as ScimIntegration[]) || [];
                return integrations;
            });
    }

    async getIntegration(id: string): Promise<ScimIntegration> {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const url = `${this.apiUrl}integrations/${id}`;
        return this.http
            .get(url, {
                headers: {
                    accept: 'application/hal+json',
                },
                
            })
            .toPromise()
            .then((result: any) => {
                const integration: any = (result as ScimIntegration) || undefined;
                return integration;
            });
    }

    private getOptions() {
        const options: PagedGetOption = {
            headers: {
                accept: 'application/hal+json',
            },
            params: { 'name.networkId': this.currentNetwork.value.id, sort: `${this.sorting},${this.ordering}` },
            pageParams: {
                page: this.page > 0 ? this.page - 1 : 0,
                size: this.pageSize,
                
            },
            sort: this.paramList.sort,
        };
        
        if (this.filterString || this.columnFilters.name) {
            let filterVal = this.filterString;
            if (!_.isEmpty(this.columnFilters.name)) {
                filterVal = this.columnFilters.name;
            }
            const escapedChars = filterVal.replace('%', '\\%').replace('_', '\\_');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            options.params['name.name'] = `%${escapedChars}%`;
        }

         if (this.columnFilters.integrationKey) {
            const esc = this.columnFilters.integrationKey.replace('%', '\\%'); // .replace('_', '\\_');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            options.params['integrationKey'] = `%${esc}%`;
        }
        return options;
    }

    isAnyToggled() {
        return this.anyToggled;
    }

    toggleAll(displayedItems: any[]): string[] {
        if (this.featureSvc.enableDeleteProtection) {
            return [];
        }

        this.allToggled = this.allToggled ||this.selectedRecordIds.length === displayedItems.length;
        
        this.allToggled = !this.allToggled;
        this.anyToggled = this.allToggled;
        const selectedRecordIds = [];
        for (const item of displayedItems) {
            if (this.authorizationService.canDeleteIntegrations(item.id)) {
                item.selected = this.allToggled;
                if (this.allToggled) {
                    selectedRecordIds.push(item.id);
                }
            }
        }
        this.selectedRecordIds = selectedRecordIds;
        this.isDeleteAction = this.anyToggled && this.selectedRecordIds.length > 0;
        
        return selectedRecordIds;
    }

    toggle(item: ScimIntegration) {
        if (this.authorizationService.canDeleteIntegrations(item.id)) {
            item.selected = !item.selected;
            this.enableDeleteProtection = this.featureSvc.enableDeleteProtection;

            if (this.allToggled && !item.selected) {
                this.allToggled = !this.allToggled
            }
            
            const index = this.selectedRecordIds.indexOf(item.id);
            if (index > -1) {
                this.selectedRecordIds.splice(index, 1);
            } else {
                this.selectedRecordIds.push(item.id);
            }

            this.anyToggled = this.selectedRecordIds.length > 0;

            this.isDeleteAction = this.anyToggled;

            if (!this.enableDeleteProtection) {
                this.isDeleteAction = true;
                this.hideAppButton = false;
            }
            if (this.selectedRecordIds.length > 1 && this.enableDeleteProtection) {
                this.isDeleteAction = false;
                this.hideAppButton = true;
            }
            if (this.selectedRecordIds.length === 1 && this.enableDeleteProtection) {
                this.isDeleteAction = true;
                this.hideAppButton = false;
            }
            if (this.selectedRecordIds.length === 0) {
                this.hideAppButton = false;
                this.isDeleteAction = false;
            }
        }
        return this.selectedRecordIds;
    }

    canCreate() {
        return this.authorizationService.canCreateIntegrations(undefined, this.currentNetwork.getValue().id) && !this.apiService.isNetworkClusterReadOnly();
    }

    canUpdate(integrationId: string) {
        return this.authorizationService.canUpdateIntegration(integrationId, undefined, this.currentNetwork.getValue().id) && !this.apiService.isNetworkClusterReadOnly();
    }

    networkReady() {
        return this.currentNetwork.value.status === 'PROVISIONED';
    }

    async save(model: ScimIntegration): Promise<ScimIntegration[]> {
        const results = await this.validate(model);
        if (!_.isArray(results) || results.length === 0) {
            if (!model.id) {
                const savePromise = new Promise<any>((resolve) => {
                    this.createResource({ body: model })
                    .pipe(take(1))
                    .subscribe({
                        next: (newResource) => {
                            this.debouncedRefreshRecords();
                            resolve(newResource);
                        },
                        error: (err) => {
                            if(err.status === 400) {
                                resolve([{ invalid: err.error.errors[0] }]);
                            } else {
                                resolve([{ save: err }]);
                            }
                            
                        },
                    });
                })
                
                return await savePromise.then((result) => {
                    return result;
                })
            } else {
                const scimIntegrationUpdateModel = new ScimIntegrationUpdate();
                scimIntegrationUpdateModel.name = model.name;
                const updatePromise = new Promise<any>((resolve) => {
                    this.patchResourceById(model.id, {body: scimIntegrationUpdateModel})
                        .pipe(take(1))
                        .subscribe({
                            next: (updatedResource) => {
                                this.debouncedRefreshRecords();
                                resolve(updatedResource);
                            },
                            error: (err) => {
                                if(err.status === 400) {
                                    resolve([{ invalid: err.error.errors[0] }]);
                                } else {
                                    resolve([{ save: err }]);
                                }
                            },
                        });
                });

                return await updatePromise.then((result) =>{ 
                    return result;
                })
            }
        } else {
            return results;
        }
    }

    createAuthToken(integrationId: string, authRequest: IntegrationAuthRequest) {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const url = `${this.apiUrl}integrations/${integrationId}/auth`;
        return new Promise<any>((resolve, reject) => {
            this.http
            .post(url, authRequest, {
                headers: headers
            })
            .pipe(take(1))
            .subscribe({
                next: (result)  => {
                    const authResponse: any = (result as IntegrationAuthResponse) || undefined;
                    resolve(authResponse);
                },
                error: (err) => {
                    reject(err.error);
                }
            });
        });
    }

    rotateAuthToken(integrationId: string) {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const url = `${this.apiUrl}integrations/${integrationId}/auth/rotate`;
        return new Promise<any>((resolve, reject) => {
            this.http.post(url, {}, {
                headers: headers
            })
            .pipe(take(1))
            .subscribe({
                next: (result)  => {
                    const authResponse: any = (result as IntegrationAuthResponse) || undefined;
                    resolve(authResponse);
                },
                error: (err) => {
                    reject(err.error);
                }
            });
        });
    }

    validate(model: ScimIntegration): any {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        if (model?.id) {
            const scimIntegrationUpdateModel = new ScimIntegrationUpdate();
            scimIntegrationUpdateModel.name = model.name;
            return lastValueFrom(
                this.http
                    .patch(this.apiUrl + 'integrations/' + model.id, scimIntegrationUpdateModel, {
                        headers: headers,
                        responseType: 'json',
                    })
                    .pipe(catchError((err) => of([{ http: err }])))
            );
        } else if (model) {
            return lastValueFrom(
                this.http
                    .post(this.apiUrl + 'integrations', model, {
                        headers: headers,
                        responseType: 'json',
                    })
                    .pipe(catchError((err) => of([{ http: err }])))
            );
        } else return Promise.resolve([{ invalid: 'integration model cannot be undefined' }]);
    }

    getAllowedActions(integrationId: string): any[] {
        const actions = [];

        if (this.authorizationService.canUpdateIntegration(integrationId)) {
            actions.push('update');
        }
        if (this.authorizationService.canDeleteIntegrations(integrationId)) {
            actions.push('delete');
        }
        return actions;
    }

    confirmAction(action: string, item: ScimIntegration) {
        this.allToggled = false;
        this.selectedRecordIds = [item.id];
        (item as any).selected = true;
        this.confirmDelete.next(item);
    }

    async delete(event: any): Promise<any[]> {
        this.isLoading.next(true);
        return new Promise<any[]>((resolve, reject) => {
            const promises: Promise<any>[] = [];
            if (event) {
                for (const item of this.filteredRecords.getValue()) {
                    if ((item as any).selected) {
                        promises.push(lastValueFrom(this.deleteResource(item)));
                    }
                }
                Promise.all(promises)
                    .then((resp) => {
                        resolve(resp);
                        this.isLoading.next(false);
                        this.selectedRecordIds = [];
                        this.isDeleteAction = false;
                        this.refreshRecords();
                        this.allToggled = false;
                    })
                    .catch((err) => {
                        this.logger.error(err);
                        this.isLoading.next(false);
                        this.selectedRecordIds = [];
                        this.isDeleteAction = false;
                        this.refreshRecords();
                        reject(err?.error?.errors[0]);
                    });
            } else {
                resolve([]);
            }
        });
    }

    tableFilterChanged(event: any) {
        if (event.columnId === 'name') {
            this.filterString = event.value;
        }
        _.set(this.columnFilters, event.columnId, event.value);
        this.clearSelected()
        this.filterHasChanged.next(true);
        this.debouncedRefreshRecords();
    }

    clearSelected() {
        this.selectedRecordIds = [];
        this.allToggled = false;
        this.anyToggled = false;
        this.isDeleteAction = false;
    }
}
